import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Accordion, Checkbox, Divider, Form, Icon, Input, Popup } from "semantic-ui-react";
import CampaignsService from "../../../services/campaign";
import { walmartMXTranslations as t } from "../../../translations/walmartMXTranslations";
import "./BrandLiftQuestions.css";

const tooltipContent = (
  <div>
    <p>{t.brandLiftTooltipParagraph1}</p>
    <p>{t.brandLiftTooltipParagraph2}</p>
    <p>{t.brandLiftTooltipParagraph3}</p>
  </div>
);

const BrandLiftQuestions = ({ values, errors, onChange, updateValues, disabled = false }) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const questionsRef = useRef([]);

  // Fetch questions once on mount
  useEffect(() => {
    if (questionsRef.current.length > 0) {
      setQuestions(questionsRef.current);
      setIsLoading(false);
      return;
    };
    const campaignsService = new CampaignsService();
    (async () => {
      try {
        const response = await campaignsService.getBrandLiftQuestions();
        questionsRef.current = response.data || [];
        setQuestions(response.data || []);
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Initialize question_ids with required IDs once questions are loaded,
  // but only when there are no pre-existing selections (new campaign).
  // Runs after the questions state update settles so values.question_ids is current.
  useEffect(() => {
    if (questions.length === 0) return;
    if ((values.question_ids || []).length > 0) return;
    const requiredIds = questions.filter((q) => q.required).map((q) => q.id);
    updateValues({ question_ids: requiredIds });
  }, [questions]);

  const requiredQuestions = questions.filter((q) => q.required);
  const optionalQuestions = questions.filter((q) => !q.required);

  const currentQuestionIds = values.question_ids || [];
  const selectedOptionalCount = currentQuestionIds.filter((id) =>
    optionalQuestions.some((q) => q.id === id),
  ).length;
  const maxOptionalReached = selectedOptionalCount >= 2;

  const brandLiftFields = ["your_brand", "competitor_1", "competitor_2", "competitor_3", "question_ids"];
  const hasErrors = brandLiftFields.some((f) => errors.hasOwnProperty(f));

  const handleOptionalToggle = (questionId) => {
    const isSelected = currentQuestionIds.includes(questionId);
    const newIds = isSelected
      ? currentQuestionIds.filter((id) => id !== questionId)
      : [...currentQuestionIds, questionId];
    updateValues({ question_ids: newIds });
  };

  return (
    <div className="brand-lift-accordion-wrapper">
    <Accordion
      fluid
      styled
      style={{
        marginBottom: "1em",
        ...(hasErrors && !isOpen ? { border: "1px solid #e0b4b4", boxShadow: "0 0 0 1px #e0b4b4" } : {}),
      }}
    >
      <Accordion.Title active={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <span>
          {t.brandLiftAccordionTitle}{" "}
          <Popup
            content={tooltipContent}
            inverted
            on="hover"
            trigger={<Icon name="help circle" className="cursor-help" />}
          />
        </span>
        {hasErrors && !isOpen && (
          <span style={{ color: "#9f3a38", fontSize: "0.85em", marginLeft: "0.75em" }}>
            <Icon name="exclamation circle" />
            {t.brandLiftRequiredFieldsError}
          </span>
        )}
        <Icon
          name="chevron down"
          style={{
            float: "right",
            transform: isOpen ? "rotate(-180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Accordion.Title>
      <Accordion.Content active={isOpen} style={{ backgroundColor: "#f9f9f9" }}>
        <h4 className="ui header" style={{ marginTop: "1em", display: "flex" }}>
          {t.brandLiftBrandsHeader}
          <Popup
            inverted
            content={t.brandLiftBrandsHeaderTooltip}
            size="mini"
            trigger={
              <Icon
                name="help circle"
                style={{ position: "relative", fontSize: "1rem" }}
                className="cursor-help"
              />
            }
          />
        </h4>
        <Divider />
        <Form.Field
          inline
          required
          error={errors.hasOwnProperty("your_brand")}
          style={{ marginBottom: errors.hasOwnProperty("your_brand") ? "1.5em" : "0.5em" }}
          disabled={disabled}
        >
          <label htmlFor="your_brand">{t.brandLiftYourBrandLabel}</label>
          <Input
            id="your_brand"
            name="your_brand"
            value={values.your_brand || ""}
            onChange={onChange}
            disabled={disabled}
          />
          <div className="custom-error">{errors["your_brand"]}</div>
        </Form.Field>
        <Form.Field
          inline
          required
          error={errors.hasOwnProperty("competitor_1")}
          style={{ marginBottom: errors.hasOwnProperty("competitor_1") ? "1.5em" : "0.5em" }}
          disabled={disabled}
        >
          <label htmlFor="competitor_1">{t.brandLiftCompetitor1Label}</label>
          <Input
            id="competitor_1"
            name="competitor_1"
            value={values.competitor_1 || ""}
            onChange={onChange}
            disabled={disabled}
          />
          <div className="custom-error">{errors["competitor_1"]}</div>
        </Form.Field>
        <Form.Field
          inline
          error={errors.hasOwnProperty("competitor_2")}
          style={{ marginBottom: errors.hasOwnProperty("competitor_2") ? "1.5em" : "0.5em" }}
          disabled={disabled}
        >
          <label htmlFor="competitor_2">{t.brandLiftCompetitor2Label}</label>
          <Input
            id="competitor_2"
            name="competitor_2"
            value={values.competitor_2 || ""}
            onChange={onChange}
            disabled={disabled}
          />
          <div className="custom-error">{errors["competitor_2"]}</div>
        </Form.Field>
        <Form.Field
          inline
          error={errors.hasOwnProperty("competitor_3")}
          style={{ marginBottom: errors.hasOwnProperty("competitor_3") ? "1.5em" : "0.5em" }}
          disabled={disabled}
        >
          <label htmlFor="competitor_3">{t.brandLiftCompetitor3Label}</label>
          <Input
            id="competitor_3"
            name="competitor_3"
            value={values.competitor_3 || ""}
            onChange={onChange}
            disabled={disabled}
          />
          <div className="custom-error">{errors["competitor_3"]}</div>
        </Form.Field>

        <h5 className="ui blue header" style={{ marginTop: "1.5em" }}>{t.brandLiftRequiredQuestionsHeader}</h5>
        {isLoading ? (
          <p>{t.brandLiftLoadingQuestions}</p>
        ) : (
          <Form.Group grouped>
            {requiredQuestions.map((q) => (
              <Form.Field key={q.id} style={{ marginBottom: "0.5em" }}>
                <div style={{ display: "flex", alignItems: "flex-start", opacity: 0.7 }}>
                  <Checkbox checked disabled style={{ marginTop: "2px", flexShrink: 0 }} />
                  <div style={{ marginLeft: "0.6em" }}>
                    <div style={{ fontWeight: "bold" }}>
                      {q.label_es}:{" "}
                      {q.tooltip_info_es && (
                        <Popup
                          content={q.tooltip_info_es}
                          inverted
                          size="mini"
                          trigger={<Icon name="info circle" className="cursor-help" style={{ fontSize: "0.9em" }} />}
                        />
                      )}
                    </div>
                    <div style={{ fontStyle: "italic", color: "#555", fontSize: "0.9em" }}>
                      "{q.question_es}"
                    </div>
                  </div>
                </div>
              </Form.Field>
            ))}
          </Form.Group>
        )}

        <h5 className="ui blue header" style={{ marginTop: "1.5em" }}>
          {t.brandLiftOptionalQuestionsHeader}{" "}
          <span style={{ fontWeight: "normal", fontSize: "0.85em" }}>
            ({t.brandLiftOptionalQuestionsSubtitle} — {selectedOptionalCount}/2)
          </span>
        </h5>
        {!isLoading && (
          <Form.Group grouped>
            {optionalQuestions.map((q) => {
              const isChecked = currentQuestionIds.includes(q.id);
              const isDisabled = disabled || (!isChecked && maxOptionalReached);
              return (
                <Form.Field key={q.id} disabled={isDisabled} style={{ marginBottom: "0.5em" }}>
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <Checkbox
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => handleOptionalToggle(q.id)}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <div style={{ marginLeft: "0.6em" }}>
                      <div style={{ fontWeight: "bold" }}>
                        {q.label_es}:{" "}
                        {q.tooltip_info_es && (
                          <Popup
                            content={q.tooltip_info_es}
                            inverted
                            size="mini"
                            trigger={<Icon name="info circle" className="cursor-help" style={{ fontSize: "0.9em" }} />}
                          />
                        )}
                      </div>
                      <div style={{ fontStyle: "italic", color: "#555", fontSize: "0.9em" }}>
                        "{q.question_es}"
                      </div>
                      {q.sub_items_es && (
                        <ul style={{ margin: "0.3em 0 0", paddingLeft: "1.2em" }}>
                          {q.sub_items_es.map((item, i) => (
                            <li key={i} style={{ fontSize: "0.9em", color: "#555" }}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </Form.Field>
              );
            })}
          </Form.Group>
        )}
        {errors.hasOwnProperty("question_ids") ? (
          <div className="custom-error" style={{ display: "block", marginLeft: 0, color: "red", fontSize: "0.85em", marginTop: "0.5em" }}>
            {errors["question_ids"]}
          </div>
        ) : (
          <p style={{ fontSize: "0.85em", color: "#888", marginTop: "0.5em" }}>{t.brandLiftRequiredLabel}</p>
        )}
      </Accordion.Content>
    </Accordion>
    </div>
  );
};

BrandLiftQuestions.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  updateValues: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default BrandLiftQuestions;
